import type { MouseEvent, MouseEventHandler, ReactNode } from 'react'
import { classNames, legacyContractDataAttrs, legacyHookAttrs, type LegacyContractName } from '@/msgdeskRuntime/legacyBridge/SelectorContracts'
import { dispatchEditorAction } from '@/msgdeskRuntime/editorActions'
import { selectEditorState } from '@/msgdeskRuntime/state'
import { useSubsStore } from '@/state/MsgTreeProvider'
import { contract, css } from '@/deskUiSkeletons/editor/Editor.helpers'
import { useEditorNameScopeValidation } from '@/deskUiSkeletons/editor/EditorTemplateContract'

function handleSave(event: MouseEvent<HTMLButtonElement>) {
  dispatchEditorAction({ action: 'save', target: event.currentTarget, reason: 'std-editor:before-save' })
}
function handleSaveDraft(event: MouseEvent<HTMLButtonElement>) {
  dispatchEditorAction({ action: 'save-draft', target: event.currentTarget, reason: 'std-editor:before-save-draft' })
}
function SubmitButton({
  legacy,
  variant = 'secondary',
  legacyClassName,
  children,
  onClick,
  pressed,
  title,
  disabled,
}: {
  legacy?: LegacyContractName
  variant?: 'primary' | 'secondary'
  legacyClassName?: string
  children: ReactNode
  onClick?: MouseEventHandler<HTMLButtonElement>
  pressed?: boolean
  title?: string
  disabled?: boolean
}) {
  const className = classNames(
    'md-editor__action inline-flex h-10 cursor-pointer items-center gap-2 rounded-md px-4 text-[13px]',
    variant === 'primary' && classNames('md-editor__action--primary', css('editor.actions.buttonPrimary')),
    variant === 'primary'
      ? 'bg-[#2563eb] font-semibold text-white hover:bg-[#1d4ed8]'
      : pressed
        ? 'md-editor__action--pressed border border-[#2563eb] bg-[#e8f0ff] font-semibold text-[#2563eb] hover:bg-[#dbe8ff]'
        : 'border border-[#c8d2dc] bg-white text-[#314253] hover:bg-[#edf2f6]',
    css('editor.actions.button'),
    legacyClassName,
  )

  return (
    <button
      type="button"
      {...(legacy ? contract(legacy) : {})}
      className={className}
      onClick={onClick}
      aria-pressed={pressed}
      title={title}
      disabled={disabled}
    >
      {children}
    </button>
  )
}

function HiddenSubmit({ legacy, label }: { legacy: LegacyContractName; label: string }) {
  return <button type="button" {...contract(legacy)} className="md-editor__hidden-submit hidden">{label}</button>
}

// The Configure/Attachments buttons open the shared React modals; the legacy
// `toggle-settings` inline swap is retired on std. A hidden compat span keeps
// the moreSectionButton contract addressable — legacy's show_form still writes
// its label/styles there (mailbox ships without it entirely; proven harmless).
export function EditorActions({
  attachCount,
  onOpenAttachments,
  onOpenConfigure,
}: {
  attachCount: number
  onOpenAttachments: () => void
  onOpenConfigure: () => void
}) {
  // Legacy m35 semantics: Draft ARMS drafting mode; Save performs the draft
  // save while armed. The armed state used to be invisible (audit D3) — now
  // the Draft button shows pressed and Save relabels.
  const { state } = useSubsStore()
  const isDrafting = selectEditorState(state).formModel?.isDraftingMode === true
  const namespace = useEditorNameScopeValidation()

  return (
    <div {...legacyHookAttrs('editor.actions', 'md-editor__actions flex flex-wrap items-center gap-2 border-t border-[#e2e8ee] bg-[#f7f9fb] px-4 py-3')}>
      <SubmitButton
        legacy="editor.action.save"
        variant="primary"
        onClick={handleSave}
        disabled={!namespace.allowed}
        title={isDrafting ? 'Drafting mode is on: saves into Drafts' : undefined}
      >
        <div className={css('editor.actions.buttonLabel')}>{isDrafting ? 'Save draft' : 'Save'}</div>
      </SubmitButton>
      <SubmitButton
        legacy="editor.action.draft"
        legacyClassName={css('editor.actions.buttonDraft')}
        onClick={handleSaveDraft}
        pressed={isDrafting}
        title={isDrafting ? 'Drafting mode on — click to turn off' : 'Turn on drafting mode (Save will save into Drafts)'}
      >
        <div className={css('editor.actions.buttonLabel')}>Draft</div>
      </SubmitButton>
      <SubmitButton onClick={onOpenAttachments}>
        <div className={css('editor.actions.buttonLabel')}>Attachments</div>
        {attachCount > 0 ? (
          <span className="md-editor__action-count inline-flex h-5 min-w-5 items-center justify-center rounded-full bg-[#e8f0ff] px-1.5 text-[11px] font-semibold text-[#2563eb]">
            {attachCount}
          </span>
        ) : null}
      </SubmitButton>
      <SubmitButton
        legacyClassName={css('editor.actions.buttonSettings')}
        onClick={onOpenConfigure}
      >
        <div className={css('editor.actions.buttonLabel')}>Configure</div>
      </SubmitButton>
      {/* Wrapper absorbs legacy's `.parent()` background writes invisibly. */}
      <span className="md-editor__more-compat-wrap hidden" hidden aria-hidden="true">
        <span {...legacyContractDataAttrs('editor.actions.moreSectionButton')} className="md-editor__more-compat" />
      </span>
      <HiddenSubmit legacy="editor.action.delete" label="Delete" />
      <HiddenSubmit legacy="editor.action.clear" label="Clear" />
      <HiddenSubmit legacy="editor.action.saveAndGo" label="Save and go" />
      <HiddenSubmit legacy="editor.action.restore" label="Restore" />
    </div>
  )
}
